import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { provideAuth, OidcSecurityService } from 'angular-auth-oidc-client';
import { take } from 'rxjs';
import { routes } from './app.routes';
import { createAuthConfig } from './auth/auth.config';
import { authInterceptor } from './auth/auth.interceptor';
import { environment } from '../environments/environment';
import { TranslationService } from './services/translation.service';

// Initialize translations on app startup — only if already authenticated
function initializeTranslations(translationService: TranslationService, oidcSecurityService: OidcSecurityService) {
  return () =>
    new Promise<void>((resolve) => {
      oidcSecurityService.isAuthenticated$.pipe(take(1)).subscribe(({ isAuthenticated }) => {
        if (!isAuthenticated) {
          console.log('[APP_INIT] Skipping translations (not authenticated)');
          resolve();
          return;
        }
        const savedLanguage = translationService.getStoredLanguage();
        console.log(`[APP_INIT] Loading translations for language: ${savedLanguage}`);
        translationService.loadTranslations(savedLanguage).then(() => resolve(), () => resolve());
      });
    });
}

// Initialize OIDC library on app startup
function initializeAuth(oidcSecurityService: OidcSecurityService) {
  // Skip auth initialization in dev mode
  if (environment.skipAuth) {
    console.log('[APP_INIT] Skipping auth (dev mode)');
    return () => Promise.resolve();
  }

  // Skip checkAuth on callback URL - let CallbackComponent handle it
  // This prevents consuming the authorization code before the component loads
  if (window.location.pathname.includes('AuthenticationCallback')) {
    console.log('[APP_INIT] On callback URL, skipping checkAuth (will be handled by CallbackComponent)');
    return () => Promise.resolve();
  }

  return () =>
    new Promise<void>((resolve) => {
      console.log('[APP_INIT] Starting auth initialization...');

      // Add timeout to prevent infinite blocking
      const timeout = setTimeout(() => {
        console.warn('[APP_INIT] Auth initialization timed out after 5s, continuing...');
        resolve();
      }, 5000);

      oidcSecurityService.checkAuth().subscribe({
        next: (result) => {
          clearTimeout(timeout);
          console.log('[APP_INIT] Auth initialized:', result.isAuthenticated ? 'authenticated' : 'not authenticated');
          console.log('[APP_INIT] Full result:', JSON.stringify(result));
          resolve();
        },
        error: (err) => {
          clearTimeout(timeout);
          console.error('[APP_INIT] Auth initialization error:', err);
          resolve(); // Resolve anyway to not block app startup
        },
      });
    });
}

export function createAppConfig(): ApplicationConfig {
  return {
    providers: [
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes),
      provideHttpClient(withInterceptors([authInterceptor])),
      provideAuth(createAuthConfig()),
      {
        provide: APP_INITIALIZER,
        useFactory: (oidcSecurityService: OidcSecurityService) => initializeAuth(oidcSecurityService),
        deps: [OidcSecurityService],
        multi: true,
      },
      {
        provide: APP_INITIALIZER,
        useFactory: (translationService: TranslationService, oidcSecurityService: OidcSecurityService) =>
          initializeTranslations(translationService, oidcSecurityService),
        deps: [TranslationService, OidcSecurityService],
        multi: true,
      },
    ],
  };
}
